import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Box, 
  Typography, 
  IconButton, 
  Chip, 
  Paper,
  Divider,
  Avatar,
  alpha,
  useTheme,
  LinearProgress
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Info as InfoIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Build as BuildIcon,
  Memory as MemoryIcon,
  SignalCellular4Bar as SignalIcon,
  Battery90 as BatteryIcon,
  Warning as WarningIcon,
  History as HistoryIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart
} from 'recharts';

/**
 * IoTDetailDrawer Component
 * 
 * Displays the details of a selected IoT sensor in a side drawer.
 * 
 * Props:
 *  - open (boolean): Controls the visibility of the drawer.
 *  - onClose (function): Callback function when the close button is clicked.
 *  - sensor (object | null): The selected sensor object containing details to display.
 */
const IoTDetailDrawer = ({ open, onClose, sensor }) => {
  const theme = useTheme();
  // State to track water level history
  const [waterLevelHistory, setWaterLevelHistory] = useState([]);
  
  // Update history when sensor value changes
  useEffect(() => {
    if (sensor) {
      // Add new reading to history
      const newReading = {
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
        value: sensor.value,
        timestamp: Date.now()
      };
      
      // Keep only the last 10 readings (or another suitable number)
      setWaterLevelHistory(prevHistory => {
        const updatedHistory = [...prevHistory, newReading];
        // Remove old entries if we have more than 10
        return updatedHistory.slice(-10);
      });
    }
  }, [sensor?.value]);
  
  // Clear history when drawer closes
  useEffect(() => {
    if (!open) {
      setWaterLevelHistory([]);
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '380px',
          maxWidth: '90%',
          boxSizing: 'border-box',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px 0 0 16px',
          overflow: 'hidden', // Ensure sticky elements don't overflow
        },
      }}
    >
      {sensor ? (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Sticky Banner/Image Area with Close Button */}
          <Box sx={{ 
            height: '220px', 
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            backgroundImage: sensor.status === 'Normal' 
              ? 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)' 
              : sensor.status === 'Warning'
                ? 'linear-gradient(135deg, #ff9800 0%, #ed6c02 100%)'
                : 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Subtle pattern overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'20\' height=\'20\' viewBox=\'0 0 20 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\' fill-rule=\'evenodd\'%3E%3Ccircle cx=\'3\' cy=\'3\' r=\'3\'/%3E%3Ccircle cx=\'13\' cy=\'13\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E")',
              backgroundSize: '12px 12px',
            }}/>
            
            {/* Gradient overlay */}
            <Box sx={{
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.5) 100%)',
            }}/>
            
            {/* Close button */}
            <IconButton 
              size="small" 
              onClick={onClose} 
              sx={{ 
                position: 'absolute', top: 16, right: 16,
                color: 'white', 
                bgcolor: 'rgba(0,0,0,0.2)',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.4)' },
                transition: 'all 0.2s ease',
                zIndex: 2
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            
            {/* Reading value - top half */}
            <Box sx={{ 
              flex: '1 0 55%', 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <Box sx={{
                textAlign: 'center',
              }}>
                <Typography variant="h1" sx={{ 
                  fontSize: '3.8rem',
                  lineHeight: 1.1,
                  fontWeight: 700, 
                  color: 'white', 
                  textShadow: '0px 2px 8px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.02em',
                }}>
                  {sensor.value}{sensor.unit}
                </Typography>
                <Typography variant="subtitle2" sx={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                  fontWeight: 500,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  fontSize: '0.7rem'
                }}>
                  Current Reading
                </Typography>
              </Box>
            </Box>

            {/* Sensor title - bottom half */}
            <Box sx={{ 
              flex: '1 0 45%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              position: 'relative',
              zIndex: 1,
              p: 2.5,
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: 'white', 
                textShadow: '0px 1px 3px rgba(0,0,0,0.5)',
                mb: 1
              }}>
                {sensor.name}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip 
                  label={sensor.type} 
                  size="small"
                  sx={{ 
                    height: '24px', 
                    fontWeight: 500,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    backdropFilter: 'blur(4px)',
                    borderRadius: '12px'
                  }}
                />
                
                <Chip 
                  label={sensor.status} 
                  size="small" 
                  icon={sensor.status !== 'Normal' ? <WarningIcon style={{ fontSize: '0.85rem' }} /> : undefined}
                  sx={{ 
                    height: '24px', 
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    bgcolor: sensor.status === 'Normal' 
                      ? 'rgba(255,255,255,0.2)' 
                      : 'rgba(255,255,255,0.3)',
                    color: 'white',
                    borderRadius: '12px',
                    backdropFilter: 'blur(4px)'
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          {/* Scrollable Content Area */}
          <Box sx={{ 
            flexGrow: 1, 
            overflowY: 'auto',
            height: 'calc(100% - 220px)', // Subtract sticky header height 
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '3px',
            }
          }}>
            {/* Sensor Status Section */}
            <Box sx={{ 
              p: 3, 
              background: 'white'
            }}>
              {/* Water Level History Graph */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <HistoryIcon sx={{ fontSize: 18, color: theme.palette.error.main, mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Water Level History
                  </Typography>
                </Box>
                
                <Box sx={{ height: 200, width: '100%', mt: 1 }}>
                  {waterLevelHistory.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={waterLevelHistory}
                        margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8}/>
                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.divider, 0.5)} />
                        <XAxis 
                          dataKey="time" 
                          tick={{ fontSize: 10 }}
                          tickLine={false}
                          axisLine={{ stroke: alpha(theme.palette.divider, 0.7) }}
                        />
                        <YAxis 
                          domain={[11, 15]} // Fixed domain for better visualization
                          tick={{ fontSize: 10 }}
                          tickCount={5}
                          tickLine={false}
                          axisLine={{ stroke: alpha(theme.palette.divider, 0.7) }}
                          tickFormatter={(value) => `${value}m`}
                        />
                        <RechartsTooltip 
                          formatter={(value) => [`${value}m`, 'Water Level']}
                          labelFormatter={(time) => `Time: ${time}`}
                          contentStyle={{ 
                            borderRadius: 8, 
                            border: 'none', 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            fontSize: 12
                          }}
                        />
                        <ReferenceLine y={13.0} stroke={theme.palette.warning.main} strokeDasharray="3 3" />
                        <ReferenceLine y={13.5} stroke={theme.palette.error.main} strokeDasharray="3 3" />
                        <Area 
                          type="monotone" 
                          dataKey="value" 
                          stroke={theme.palette.primary.main} 
                          strokeWidth={2}
                          fill="url(#colorValue)"
                          activeDot={{ 
                            r: 6, 
                            fill: theme.palette.primary.dark,
                            strokeWidth: 2,
                            stroke: '#fff'
                          }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexDirection: 'column',
                      bgcolor: alpha(theme.palette.background.paper, 0.4),
                      borderRadius: 1,
                      p: 2
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                        Collecting data points...
                      </Typography>
                      <LinearProgress sx={{ width: '50%' }} />
                    </Box>
                  )}
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 1, 
                  gap: 2,
                  fontSize: '0.75rem',
                  color: 'text.secondary'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 10, 
                      height: 3, 
                      bgcolor: theme.palette.warning.main, 
                      mr: 0.5,
                      borderRadius: 1
                    }} />
                    <Typography variant="caption">Warning (13.0m)</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                      width: 10, 
                      height: 3, 
                      bgcolor: theme.palette.error.main, 
                      mr: 0.5,
                      borderRadius: 1
                    }} />
                    <Typography variant="caption">Critical (13.5m)</Typography>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3
              }}>
                <Box sx={{ 
                  p: 2, 
                  flex: 1,
                  bgcolor: alpha(theme.palette.success.light, 0.08), 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <BatteryIcon sx={{ color: theme.palette.success.main, fontSize: '1.75rem', mb: 1 }} />
                  <Typography variant="overline" sx={{ color: alpha(theme.palette.text.secondary, 0.8), fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em' }}>
                    BATTERY
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.success.main, mt: 0.5 }}>
                    {sensor.batteryLevel}
                  </Typography>
                </Box>
                
                <Box sx={{ 
                  p: 2, 
                  flex: 1,
                  bgcolor: alpha(theme.palette.info.light, 0.08), 
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <SignalIcon sx={{ color: theme.palette.info.main, fontSize: '1.75rem', mb: 1 }} />
                  <Typography variant="overline" sx={{ color: alpha(theme.palette.text.secondary, 0.8), fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em' }}>
                    SIGNAL
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.info.main, mt: 0.5 }}>
                    {sensor.signalStrength}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ 
                p: 2,
                mb: 3, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.7)}`
              }}>
                <Box>
                  <Typography variant="overline" sx={{ color: alpha(theme.palette.text.secondary, 0.8), fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', display: 'block' }}>
                    ALERT THRESHOLD
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.warning.main, mt: 0.5 }}>
                    {sensor.alertThreshold}
                  </Typography>
                </Box>
                <Chip 
                  label={sensor.status} 
                  icon={sensor.status !== 'Normal' ? <WarningIcon fontSize="small" /> : undefined}
                  size="small" 
                  color={sensor.status === 'Normal' ? 'success' : sensor.status === 'Warning' ? 'warning' : 'error'}
                  sx={{ fontWeight: 600, px: 0.5 }}
                />
              </Box>
              
              <Box sx={{ 
                p: 2.5, 
                bgcolor: sensor.status === 'Normal' 
                  ? alpha(theme.palette.success.light, 0.1)
                  : sensor.status === 'Warning'
                    ? alpha(theme.palette.warning.light, 0.1)
                    : alpha(theme.palette.error.light, 0.1),
                borderRadius: 2,
                border: `1px solid ${
                  sensor.status === 'Normal' 
                    ? alpha(theme.palette.success.main, 0.2)
                    : sensor.status === 'Warning'
                      ? alpha(theme.palette.warning.main, 0.2) 
                      : alpha(theme.palette.error.main, 0.2)
                }`
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                    Current Water Level
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 700, 
                    color: sensor.status === 'Normal' 
                      ? theme.palette.success.main
                      : sensor.status === 'Warning'
                        ? theme.palette.warning.main 
                        : theme.palette.error.main
                  }}>
                    {sensor.value}{sensor.unit}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(sensor.value / 15) * 100} // Assuming 15m is max level
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: sensor.value > 13.5 ? theme.palette.error.main :
                                       sensor.value > 13.0 ? theme.palette.warning.main :
                                       theme.palette.success.main,
                      borderRadius: 5,
                    }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600 }}>
                    Normal (&lt;13.0m)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.warning.main, fontWeight: 600 }}>
                    Warning (13.0-13.5m)
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
                    Critical (&gt;13.5m)
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Location */}
            <Box sx={{ px: 3, pb: 3, bgcolor: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ fontSize: 18, color: theme.palette.error.main, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Location
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '120px 1fr', 
                rowGap: 1.5, 
                columnGap: 1.5, 
                mb: 3,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Description:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.location.description}</Typography>
                
                <Typography variant="body2" color="text.secondary">Coordinates:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {sensor.location.latitude.toFixed(5)}, {sensor.location.longitude.toFixed(5)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2, borderColor: alpha(theme.palette.divider, 0.5) }} />
              
              {/* Technical Details */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MemoryIcon sx={{ fontSize: 18, color: theme.palette.error.main, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Technical Specifications
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '120px 1fr', 
                rowGap: 1.5, 
                columnGap: 1.5, 
                mb: 3,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Model:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.modelNumber}</Typography>
                
                <Typography variant="body2" color="text.secondary">Manufacturer:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.manufacturer}</Typography>
                
                <Typography variant="body2" color="text.secondary">Data Frequency:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.dataFrequency}</Typography>
                
                <Typography variant="body2" color="text.secondary">Installation Date:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.installationDate}</Typography>
              </Box>
              
              <Divider sx={{ my: 2, borderColor: alpha(theme.palette.divider, 0.5) }} />
              
              {/* Maintenance */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 18, color: theme.palette.error.main, mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Maintenance Status
                </Typography>
              </Box>
              
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: '150px 1fr', 
                rowGap: 1.5, 
                columnGap: 1.5,
                '& .MuiTypography-root': { lineHeight: 1.5 }
              }}>
                <Typography variant="body2" color="text.secondary">Current Status:</Typography>
                <Chip 
                  label={sensor.maintenanceStatus} 
                  size="small" 
                  color="success"
                  sx={{ height: 22, fontSize: '0.75rem', fontWeight: 500 }}
                />
                
                <Typography variant="body2" color="text.secondary">Last Maintenance:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{sensor.lastMaintenance}</Typography>
                
                <Typography variant="body2" color="text.secondary">Last Reading Update:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {new Date(sensor.lastUpdated).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        // Empty state when no sensor is selected
        <Box sx={{ 
          p: 4, 
          textAlign: 'center', 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: 2,
          height: '100%',
          bgcolor: 'white'
        }}>
          <Avatar sx={{ 
            width: 80, 
            height: 80, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            mb: 2
          }}>
            <MemoryIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />
          </Avatar>
          
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            No IoT sensor selected
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '80%', mb: 2 }}>
            Click on any IoT sensor on the map to view detailed information
          </Typography>
          
          <Box sx={{
            p: 2,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            borderRadius: 2,
            border: `1px dashed ${alpha(theme.palette.error.main, 0.2)}`,
            width: '80%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ fontSize: 16, color: theme.palette.error.main, mr: 1 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                Sensor Information
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Real-time flood monitoring sensors
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Sends alerts when water levels exceed thresholds
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'left', pl: 3.5, mt: 0.5 }}>
              • Battery-powered with wireless connectivity
            </Typography>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default IoTDetailDrawer; 